import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PhoneInput } from "@/components/PhoneInput";
import { Calendar, Clock, CheckCircle, ChevronLeft } from "lucide-react";
import { toRupees } from "../utils/currency";

export default function PublicBooking() {
    const { clinicId, doctorId: routeDoctorId } = useParams();
    const [searchParams] = useSearchParams();

    // State
    const [step, setStep] = useState(1); // 1: Select Doctor (if needed), 2: Select Slot, 3: Details & Book, 4: Success
    const [isLoading, setIsLoading] = useState(true);
    const [clinicInfo, setClinicInfo] = useState(null);
    const [doctors, setDoctors] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState(null);

    // Slot Selection State
    const [selectedDate, setSelectedDate] = useState("");
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [selectedTime, setSelectedTime] = useState("");

    // Booking Details State
    const [patientDetails, setPatientDetails] = useState({
        name: "",
        phone: "",
        email: "",
        age: "",
        gender: "unknown",
        date_of_birth: "",
        ageInputMode: "age"
    });

    // OTP State
    const [otpSent, setOtpSent] = useState(false);
    const [otp, setOtp] = useState("");
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);

    // Initialization
    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            try {
                if (clinicId) {
                    // Fetch clinic info
                    const cInfo = await axios.get(`${API}/public/clinics/${clinicId}/info`);
                    setClinicInfo(cInfo.data);

                    // Fetch doctors
                    const docs = await axios.get(`${API}/public/clinics/${clinicId}/doctors`);
                    setDoctors(docs.data);

                    // Check if doctor is pre-selected in URL
                    const preselectedDoctorId = searchParams.get("doctor_id");
                    if (preselectedDoctorId) {
                        const preDoc = docs.data.find(d => d.id === preselectedDoctorId);
                        if (preDoc) {
                            selectDoctor(preDoc);
                        } else {
                            setStep(1);
                        }
                    } else if (docs.data.length === 1) {
                        // Auto-select if only one doctor
                        selectDoctor(docs.data[0]);
                    } else {
                        setStep(1);
                    }
                } else if (routeDoctorId) {
                    // Personal practice route
                    const dInfo = await axios.get(`${API}/public/doctors/${routeDoctorId}/info`);
                    selectDoctor(dInfo.data);
                }
            } catch (error) {
                console.error("Initialization error:", error);
                toast.error("Failed to load booking information.");
            } finally {
                setIsLoading(false);
            }
        };

        init();
    }, [clinicId, routeDoctorId]);

    // Fetch slots when date changes
    useEffect(() => {
        if (selectedDate && selectedDoctor) {
            fetchSlots(selectedDate);
        }
    }, [selectedDate, selectedDoctor]);

    const selectDoctor = (doctor) => {
        setSelectedDoctor(doctor);
        // Set minimum date to tomorrow (to respect 24h rule)
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const minDateStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;
        setSelectedDate(minDateStr);
        setStep(2);
    };

    const fetchSlots = async (date) => {
        setSlotsLoading(true);
        setSelectedTime("");
        try {
            const params = {
                date: date,
                doctor_id: selectedDoctor.id
            };
            if (clinicId) {
                params.clinic_id = clinicId;
            }
            const res = await axios.get(`${API}/public/slots`, { params });
            setSlots(res.data || []);
        } catch (error) {
            console.error("Failed to fetch slots", error);
            setSlots([]);
        } finally {
            setSlotsLoading(false);
        }
    };

    const handleSendOTP = async (e) => {
        e.preventDefault();
        setIsSendingOtp(true);
        try {
            await axios.post(`${API}/public/book/send-otp`, {
                email: patientDetails.email,
                name: patientDetails.name
            });
            setOtpSent(true);
            toast.success("Verification code sent to your email");
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to send code. Please try again.");
        } finally {
            setIsSendingOtp(false);
        }
    };

    const handleVerifyAndBook = async (e) => {
        e.preventDefault();
        setIsVerifying(true);
        try {
            // 1. Verify OTP
            const verifyRes = await axios.post(`${API}/public/book/verify-otp`, {
                email: patientDetails.email,
                otp: otp
            });
            const { booking_token } = verifyRes.data;

            // 2. Create Booking
            const payload = {
                booking_token,
                doctor_id: selectedDoctor.id,
                date: selectedDate,
                time: selectedTime,
                patient_name: patientDetails.name,
                patient_phone: patientDetails.phone,
                patient_email: patientDetails.email,
                patient_gender: patientDetails.gender
            };
            if (patientDetails.ageInputMode === "age" && patientDetails.age) {
                payload.patient_age = Number(patientDetails.age);
                payload.patient_date_of_birth = null;
            } else if (patientDetails.ageInputMode === "dob" && patientDetails.date_of_birth) {
                payload.patient_age = null;
                payload.patient_date_of_birth = patientDetails.date_of_birth;
            } else {
                payload.patient_age = null;
                payload.patient_date_of_birth = null;
            }
            if (clinicId) {
                payload.clinic_id = clinicId;
            }

            await axios.post(`${API}/public/book`, payload);
            setStep(4);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to complete booking.");
        } finally {
            setIsVerifying(false);
        }
    };

    // Render Functions
    const renderHeader = () => {
        const title = clinicId ? clinicInfo?.name : selectedDoctor?.name;
        // Don't show header if loading
        if (isLoading) return null;

        return (
            <div className="text-center mb-6">
                {clinicInfo?.logo_url && (
                    <img src={clinicInfo.logo_url} alt="Clinic Logo" className="h-12 mx-auto mb-3 object-contain" />
                )}
                <h1 className="text-xl font-bold text-slate-900">{title || "Book an Appointment"}</h1>
                {clinicId && clinicInfo?.address && (
                    <p className="text-sm text-slate-500 mt-1">{clinicInfo.address}</p>
                )}
            </div>
        );
    };

    // STEP 1: Select Doctor
    const renderDoctorSelection = () => {
        if (doctors.length === 0) return <div className="text-center p-8 text-slate-500">No doctors available.</div>;

        return (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
                <h2 className="text-lg font-semibold text-slate-800 mb-4 text-center">Select a Doctor</h2>
                <div className="grid gap-3">
                    {doctors.map(doc => {
                        const initials = (doc.name || 'D').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                        return (
                            <div
                                key={doc.id}
                                onClick={() => selectDoctor(doc)}
                                className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-400 hover:shadow-md cursor-pointer transition-all"
                            >
                                <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold flex-shrink-0">
                                    {initials}
                                </div>
                                <div>
                                    <div className="font-semibold text-slate-900">{doc.name}</div>
                                    <div className="text-xs text-slate-500">{doc.qualification || "Consultant"}</div>
                                    {doc.consultation_fee !== undefined && doc.consultation_fee !== null ? (
                                        <div className="text-xs font-semibold text-emerald-600 mt-1">Fee: ₹{toRupees(doc.consultation_fee)}</div>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // STEP 2: Select Date & Time
    const renderSlotSelection = () => {
        // Calculate min date based on 24 hour rule
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const minDateStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;

        return (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-2">
                {clinicId && doctors.length > 1 && (
                    <button onClick={() => setStep(1)} className="flex items-center text-sm text-slate-500 hover:text-slate-800 mb-2">
                        <ChevronLeft size={16} className="mr-1" /> Back to doctors
                    </button>
                )}

                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold flex-shrink-0">
                            {selectedDoctor?.name?.charAt(0) || 'D'}
                        </div>
                        <div>
                            <div className="text-xs text-slate-500 font-medium uppercase tracking-wider">Booking with</div>
                            <div className="font-semibold text-slate-900">{selectedDoctor?.name}</div>
                        </div>
                    </div>
                </div>

                <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">1. Select Date</Label>
                    <div className="relative">
                        <Calendar className="absolute left-3 top-2.5 text-slate-400 pointer-events-none" size={18} />
                        <Input
                            type="date"
                            min={minDateStr}
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="pl-10 pb-1"
                        />
                    </div>
                    <p className="text-xs text-slate-500 mt-2 italic">Appointments must be booked at least 24 hours in advance.</p>
                </div>

                {selectedDate && (
                    <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-3 block">2. Select Time</Label>
                        {slotsLoading ? (
                            <div className="grid grid-cols-3 gap-2">
                                {[1, 2, 3, 4, 5, 6].map(i => (
                                    <div key={i} className="h-10 bg-slate-100 animate-pulse rounded-md"></div>
                                ))}
                            </div>
                        ) : slots.length > 0 ? (
                            <div className="grid grid-cols-3 gap-2 max-h-[200px] overflow-y-auto pr-1 pb-1 custom-scrollbar">
                                {slots.map(time => (
                                    <div
                                        key={time}
                                        onClick={() => setSelectedTime(time)}
                                        className={`flex items-center justify-center py-2 px-1 rounded-md text-sm cursor-pointer transition-all border ${selectedTime === time
                                            ? 'bg-blue-600 text-white border-blue-600 shadow-md transform scale-[1.02]'
                                            : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                                            }`}
                                    >
                                        <Clock size={14} className="mr-1.5 opacity-70" />
                                        {time}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center p-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                                <p className="text-slate-500 text-sm">No available slots on this date.</p>
                                <p className="text-slate-400 text-xs mt-1">Please select another date.</p>
                            </div>
                        )}
                    </div>
                )}

                <div className="pt-4 border-t border-slate-100">
                    <Button
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white shadow-lg"
                        disabled={!selectedDate || !selectedTime}
                        onClick={() => setStep(3)}
                    >
                        Continue to Details
                    </Button>
                </div>
            </div>
        );
    };

    // STEP 3: Patient Details & OTP
    const renderDetails = () => {
        // Format date for display
        const displayDate = selectedDate ? new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';

        return (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-2">
                <button onClick={() => { setStep(2); setOtpSent(false); }} className="flex items-center text-sm text-slate-500 hover:text-slate-800 mb-2">
                    <ChevronLeft size={16} className="mr-1" /> Back to slot selection
                </button>

                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex justify-between items-center text-sm">
                    <div>
                        <div className="text-blue-800 font-semibold">{displayDate}</div>
                        <div className="text-blue-600 mb-1">{selectedTime} with {selectedDoctor?.name}</div>
                        {selectedDoctor?.consultation_fee !== undefined && selectedDoctor?.consultation_fee !== null ? (
                            <div className="text-emerald-700 font-medium">Consultation Fee: ₹{toRupees(selectedDoctor.consultation_fee)}</div>
                        ) : (
                            <div className="text-emerald-700 font-medium">Consultation Fee: {clinicId ? "Contact clinic" : "Contact doctor"}</div>
                        )}
                    </div>
                </div>

                <h2 className="text-lg font-bold text-slate-800">Your Details</h2>

                {!otpSent ? (
                    <form onSubmit={handleSendOTP} className="space-y-4">
                        <div>
                            <Label>Full Name *</Label>
                            <Input
                                value={patientDetails.name}
                                onChange={(e) => setPatientDetails({ ...patientDetails, name: e.target.value })}
                                required
                                placeholder="John Doe"
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <Label>Age / Date of Birth (Optional)</Label>
                                <div style={{ display: 'flex', background: '#f1f5f9', padding: '0.25rem', borderRadius: '8px', width: 'fit-content' }}>
                                    <button
                                        type="button"
                                        onClick={() => setPatientDetails({ ...patientDetails, ageInputMode: 'age', date_of_birth: '' })}
                                        style={{
                                            padding: '0.25rem 0.75rem',
                                            borderRadius: '6px',
                                            fontSize: '0.875rem',
                                            fontWeight: '500',
                                            background: patientDetails.ageInputMode === 'age' ? 'white' : 'transparent',
                                            boxShadow: patientDetails.ageInputMode === 'age' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: patientDetails.ageInputMode === 'age' ? '#0f172a' : '#64748b',
                                            transition: 'all 0.15s'
                                        }}
                                    >
                                        Age
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPatientDetails({ ...patientDetails, ageInputMode: 'dob', age: '' })}
                                        style={{
                                            padding: '0.25rem 0.75rem',
                                            borderRadius: '6px',
                                            fontSize: '0.875rem',
                                            fontWeight: '500',
                                            background: patientDetails.ageInputMode === 'dob' ? 'white' : 'transparent',
                                            boxShadow: patientDetails.ageInputMode === 'dob' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: patientDetails.ageInputMode === 'dob' ? '#0f172a' : '#64748b',
                                            transition: 'all 0.15s'
                                        }}
                                    >
                                        Date of Birth
                                    </button>
                                </div>
                                
                                {patientDetails.ageInputMode === 'age' ? (
                                    <Input
                                        type="number"
                                        min="0"
                                        max="120"
                                        value={patientDetails.age}
                                        onChange={(e) => setPatientDetails({ ...patientDetails, age: e.target.value })}
                                        placeholder="e.g. 30"
                                    />
                                ) : (
                                    <Input
                                        type="date"
                                        value={patientDetails.date_of_birth}
                                        onChange={(e) => setPatientDetails({ ...patientDetails, date_of_birth: e.target.value })}
                                    />
                                )}
                            </div>
                            <div>
                                <Label>Gender *</Label>
                                <select
                                    value={patientDetails.gender}
                                    onChange={(e) => setPatientDetails({ ...patientDetails, gender: e.target.value })}
                                    required
                                    className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <option value="unknown">Prefer not to say</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <Label>Phone Number *</Label>
                            <PhoneInput
                                value={patientDetails.phone}
                                onChange={(val) => setPatientDetails({ ...patientDetails, phone: val })}
                                required
                            />
                        </div>
                        <div>
                            <Label>Email Address *</Label>
                            <Input
                                type="email"
                                value={patientDetails.email}
                                onChange={(e) => setPatientDetails({ ...patientDetails, email: e.target.value })}
                                required
                                placeholder="john@example.com"
                            />
                            <p className="text-xs text-slate-500 mt-1">We will send a verification code to this email.</p>
                        </div>

                        <Button
                            type="submit"
                            disabled={isSendingOtp}
                            className="w-full bg-slate-900 hover:bg-slate-800 text-white shadow-lg mt-6"
                        >
                            {isSendingOtp ? "Sending Code..." : "Send Verification Code"}
                        </Button>
                    </form>
                ) : (
                    <form onSubmit={handleVerifyAndBook} className="space-y-6 animate-in fade-in">
                        <div className="bg-emerald-50 p-4 border border-emerald-100 rounded-lg text-sm text-emerald-800">
                            A verification code has been sent to <strong>{patientDetails.email}</strong>
                        </div>

                        <div>
                            <Label className="text-center block mb-3">Enter Verification Code</Label>
                            <Input
                                type="text"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                required
                                maxLength={6}
                                className="text-center text-2xl tracking-[0.25em] font-mono h-14"
                                placeholder="------"
                            />
                        </div>

                        <div className="flex flex-col gap-3">
                            <Button
                                type="submit"
                                disabled={isVerifying || otp.length < 6}
                                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg"
                            >
                                {isVerifying ? "Verifying..." : "Verify & Confirm Booking"}
                            </Button>

                            <Button
                                type="button"
                                variant="ghost"
                                className="text-slate-500 text-sm"
                                onClick={() => setOtpSent(false)}
                            >
                                Change email address
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        );
    };

    // STEP 4: Success
    const renderSuccess = () => {
        return (
            <div className="text-center py-8 px-4 animate-in zoom-in-95 duration-500">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                    <CheckCircle className="text-emerald-500" size={40} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Booking Requested!</h2>
                <p className="text-slate-500 mb-6 max-w-[280px] mx-auto">
                    Your appointment request for <strong>{selectedTime}</strong> has been received and is pending confirmation.
                </p>

                <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 text-left max-w-[300px] mx-auto mb-8">
                    <div className="text-sm font-medium text-slate-800 border-b border-slate-200 pb-2 mb-2">Appointment Details</div>
                    <div className="text-sm text-slate-600 grid grid-cols-[auto_1fr] gap-x-3 gap-y-2">
                        <span className="text-slate-400">Patient:</span> <span className="font-medium text-slate-800">{patientDetails.name}</span>
                        <span className="text-slate-400">Doctor:</span> <span className="font-medium text-slate-800">{selectedDoctor?.name}</span>
                        <span className="text-slate-400">Date:</span> <span>{selectedDate}</span>
                        <span className="text-slate-400">Time:</span> <span>{selectedTime}</span>
                        {clinicId && (
                            <><span className="text-slate-400">Clinic:</span> <span className="truncate">{clinicInfo?.name}</span></>
                        )}
                        <span className="text-slate-400">Status:</span> <span className="text-amber-600 font-medium">Pending Review</span>
                    </div>
                </div>

                <Button
                    variant="outline"
                    onClick={() => {
                        // Reset everything to start over
                        if (routeDoctorId || doctors.length === 1) {
                            selectDoctor(selectedDoctor);
                        } else {
                            setStep(1);
                            setSelectedDoctor(null);
                        }
                        setSelectedTime("");
                        setPatientDetails({ name: "", phone: "", email: "", age: "", gender: "unknown" }); // Reset partial state
                        setOtpSent(false);
                        setOtp("");
                    }}
                    className="mx-auto block"
                >
                    Book Another Appointment
                </Button>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="animate-pulse flex flex-col items-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full mb-4"></div>
                    <div className="h-4 w-32 bg-slate-200 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-100 py-4 sm:py-8 px-4 flex items-center justify-center font-sans" style={{ background: "transparent" }}>
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden relative" style={{ boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.05), 0 8px 10px -6px rgb(0 0 0 / 0.05)' }}>
                {/* Progress Bar */}
                {step < 4 && (
                    <div className="absolute top-0 left-0 right-0 h-1 bg-slate-100">
                        <div
                            className="h-full bg-blue-500 transition-all duration-500 ease-out"
                            style={{ width: `${(step / 3) * 100}%` }}
                        />
                        <div className="absolute top-2 right-4 text-[10px] font-medium text-slate-400 uppercase tracking-wider">
                            Step {step} of 3
                        </div>
                    </div>
                )}

                <div className="p-6 sm:p-8">
                    {step < 4 && renderHeader()}

                    {step === 1 && renderDoctorSelection()}
                    {step === 2 && renderSlotSelection()}
                    {step === 3 && renderDetails()}
                    {step === 4 && renderSuccess()}
                </div>

                {step < 4 && (
                    <div className="bg-slate-50 p-4 text-center border-t border-slate-100 text-xs text-slate-400 mt-auto">
                        Powered by <strong>ClinicNode</strong> &copy; {new Date().getFullYear()}
                    </div>
                )}
            </div>
        </div>
    );
}
